<script setup>
  import { ref, onMounted, onUnmounted } from "vue"
  import controlPad from "@/components/controlPad.vue"
  import display from "@/components/display.vue"

  const mode = ref( "Selection" ) // Default to Selection mode
  const sessionId = ref( "" )

  // Function to generate a random session ID
  const generateSessionId = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase()
  }

  // Function to handle URL changes
  const handleUrlChange = () => {
    const path = window.location.pathname
    const urlParams = new URLSearchParams( window.location.search )
    const urlSessionId = urlParams.get( 'session' )

    // Set session ID from URL or generate a new one
    if ( urlSessionId ) {
      sessionId.value = urlSessionId
    }
    else if ( !sessionId.value ) {
      sessionId.value = generateSessionId()
    }

    // Set mode based on path
    if ( path === "/control" ) {
      mode.value = "ControlPad"
    }
    else if ( path === "/display" ) {
      mode.value = "Display"
    }
    else if ( path === "/" ) {
      // For root path, show mode selection screen
      mode.value = "Selection"
    }
    else {
      // Default to Display for any other path
      mode.value = "Display"

      // Update URL to include the session ID
      const sessionParam = sessionId.value ? `?session=${sessionId.value}` : ""
      history.pushState( {}, "", `/display${sessionParam}` )
    }
  }

  // Function to set mode and update URL
  const setMode = (newMode) => {
    mode.value = newMode

    // Update URL based on selected mode
    const sessionParam = sessionId.value ? `?session=${sessionId.value}` : ""
    if (newMode === "ControlPad") {
      history.pushState( {}, "", `/control${sessionParam}` )
    }
    else if (newMode === "Display") {
      history.pushState( {}, "", `/display${sessionParam}` )
    }
  }

  // Function to reset and go home
  const goHome = () => {
    // Generate a new session ID
    sessionId.value = generateSessionId()

    // Go to mode selection screen
    mode.value = "Selection"

    // Update URL to root
    history.pushState( {}, "", `/` )
  }

  // Check URL on component mount and set up popstate listener
  onMounted( () => {
    handleUrlChange()

    // Handle browser back/forward buttons
    window.addEventListener( "popstate", handleUrlChange )
  } )

  // Clean up event listeners on component unmount
  onUnmounted( () => {
    window.removeEventListener( "popstate", handleUrlChange )
  } )
</script>

<template lang="pug">
  .full-page
    .content
      .container-fluid.mt-1.px-1.px-md-3
        // Mode selection screen
        .row.mt-5( v-if="mode === 'Selection'" )
          .col-12.d-flex.justify-content-center.flex-column.align-items-center
            h2.mb-4 Choose Mode
            .btn-group
              button.btn.btn-outline-light.btn-lg.p-4( @click="setMode('ControlPad')" ) Single
              button.btn.btn-outline-light.btn-lg.p-4( @click="setMode('Display')" ) Display

        // Component rendering - directly show control pad or display based on mode
        control-pad( v-if="mode === 'ControlPad'" :session-id="sessionId" @reset="goHome" )
        display( v-if="mode === 'Display'" :session-id="sessionId" @reset="goHome" )

</template>

<style scoped>
  .full-page {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }

  .content {
    flex-grow: 1;
    display: flex;
    align-items: top;
    justify-content: center;
  }

</style>
